import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Plus, 
  Search, 
  FileText, 
  Receipt, 
  Calendar,
  Euro,
  Eye,
  Edit,
  Trash2,
  Download,
  Send,
  X,
  Calculator
} from "lucide-react";
import { toast } from "react-hot-toast";
import DevisPDFGenerator from "./DevisPDFGenerator";

const API = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

const DevisFacturesPage = () => {
  const [documents, setDocuments] = useState([]);
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [produits, setProduits] = useState([]); // NOUVEAU: Liste des produits
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  
  // PDF Generator
  const { generatePDF } = DevisPDFGenerator();
  
  // Form states
  const [newDocument, setNewDocument] = useState({
    type: "devis",
    numero: "",
    recipient_type: "client", // "client" ou "fournisseur" - défaut client pour les devis
    client_id: "",
    supplier_id: "",
    date_echeance: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // +30 jours
    notes: "",
    items: [],
    sous_total_ht: 0,
    total_tva: 0,
    total_ttc: 0,
    // Champs pour saisie manuelle client
    manual_client_nom: "",
    manual_client_prenom: "", 
    manual_client_company: "",
    manual_client_adresse: "",
    manual_client_code_postal: "",
    manual_client_ville: ""
  });
  
  // States for adding new clients/suppliers
  const [showAddClientDialog, setShowAddClientDialog] = useState(false);
  const [showAddSupplierDialog, setShowAddSupplierDialog] = useState(false);
  const [manualClientEntry, setManualClientEntry] = useState(false);
  const [newClient, setNewClient] = useState({
    company_name: "",
    prenom: "",
    nom: "",
    email: "",
    siren: "",
    adresse: "",
    code_postal: "",
    ville: ""
  });
  const [newSupplier, setNewSupplier] = useState({
    name: "",
    address: "",
    email: "",
    type: "electricity_gas"
  });

  useEffect(() => {
    fetchDocuments();
    fetchClients();
    fetchSuppliers();
    fetchProduits(); // NOUVEAU: Charger les produits
  }, []);

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/devis-factures`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (error) {
      console.error("Error fetching documents:", error);
      toast.error("Erreur lors du chargement des documents");
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log("Clients loaded:", response.data); // Debug
      setClients(response.data);
      if (response.data.length === 0) {
        toast.error("Aucun client trouvé. Veuillez d'abord créer des clients.");
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error("Erreur lors du chargement des clients");
    }
  };

  const fetchSuppliers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/config/suppliers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log("Config suppliers loaded:", response.data); // Debug
      setSuppliers(response.data);
    } catch (error) {
      console.error("Error fetching config suppliers:", error);
      toast.error("Erreur lors du chargement des fournisseurs");
    }
  };

  // NOUVEAU: Récupérer les produits du catalogue
  const fetchProduits = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/produits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log("Produits loaded:", response.data);
      setProduits(response.data);
    } catch (error) {
      console.error("Error fetching produits:", error);
      toast.error("Erreur lors du chargement des produits");
    }
  };

  const handleCreateDocument = async () => {
    try {
      console.log("Creating/updating document with data:", newDocument);
      console.log("Recipient type:", newDocument.recipient_type);
      console.log("Client ID:", newDocument.client_id);
      console.log("Supplier ID:", newDocument.supplier_id);
      console.log("Available clients:", clients.map(c => ({id: c.id, nom: c.nom, company: c.company_name})));
      console.log("Client selected exists?", clients.find(c => c.id === newDocument.client_id));
      
      // Validate recipient selection
      if (newDocument.recipient_type === "client") {
        if (!manualClientEntry && !newDocument.client_id) {
          console.error("Client ID is missing for client recipient type");
          toast.error("Veuillez sélectionner un client");
          return;
        }
        if (manualClientEntry && (!newDocument.manual_client_nom || !newDocument.manual_client_prenom)) {
          toast.error("Veuillez renseigner au minimum le nom et prénom du client");
          return;
        }
      }
      
      if (newDocument.recipient_type === "fournisseur" && !newDocument.supplier_id) {
        toast.error("Veuillez sélectionner un fournisseur");
        return;
      }

      // Générer un numéro automatiquement si pas fourni
      if (!newDocument.numero) {
        const now = new Date();
        const prefix = newDocument.type === "devis" ? "DEV" : "FACT";
        const datePart = now.toISOString().slice(0, 10).replace(/-/g, "");
        const randomPart = Math.random().toString(36).substr(2, 4).toUpperCase();
        newDocument.numero = `${prefix}-${datePart}-${randomPart}`;
      }

      const token = localStorage.getItem('token');
      let response;
      
      if (selectedDocument && selectedDocument.id) {
        // Mode modification
        response = await axios.put(`${API}/api/devis-factures/${selectedDocument.id}`, newDocument, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Mettre à jour la liste des documents
        setDocuments(documents.map(doc => 
          doc.id === selectedDocument.id ? response.data : doc
        ));
        toast.success(`${newDocument.type === "devis" ? "Devis" : "Facture"} modifié(e) avec succès`);
      } else {
        // Mode création
        response = await axios.post(`${API}/api/devis-factures`, newDocument, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDocuments([response.data, ...documents]);
        const recipientType = newDocument.recipient_type === "fournisseur" ? "fournisseur" : "client";
        toast.success(`${newDocument.type === "devis" ? "Devis" : "Facture"} créé(e) avec succès pour le ${recipientType}`);
      }
      
      setShowCreateDialog(false);
      setSelectedDocument(null);
      setNewDocument({
        type: "devis",
        recipient_type: "client",
        client_id: "",
        supplier_id: "",
        date_echeance: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        notes: "",
        items: [],
        sous_total_ht: 0,
        total_tva: 0,
        total_ttc: 0,
        manual_client_nom: "",
        manual_client_prenom: "", 
        manual_client_company: "",
        manual_client_adresse: "",
        manual_client_code_postal: "",
        manual_client_ville: ""
      });
      setManualClientEntry(false);
      
    } catch (error) {
      console.error("Error creating/updating document:", error);
      console.error("Error details:", error.response?.data);
      toast.error(`Erreur lors de ${selectedDocument ? 'la modification' : 'la création'}: ${error.response?.data?.detail || error.message}`);
    }
  };

  const addItem = () => {
    const newItem = {
      libelle: "",
      quantite: 1,
      prix_unitaire: 0,
      taux_tva: 20,
      montant_ht: 0,
      montant_tva: 0,
      montant_ttc: 0,
      produit_id: "" // NOUVEAU: ID du produit sélectionné depuis le catalogue
    };
    setNewDocument({
      ...newDocument,
      items: [...newDocument.items, newItem]
    });
  };

  // NOUVEAU: Fonction pour remplir un item à partir d'un produit
  const selectProduitForItem = (index, produitId) => {
    const produit = produits.find(p => p.id === produitId);
    if (produit) {
      const quantite = newDocument.items[index].quantite || 1;
      const prixHT = produit.prix_vente_ht || 0;
      const tauxTVA = produit.taux_tva || 20;
      const montantHT = quantite * prixHT;
      const montantTVA = montantHT * (tauxTVA / 100);
      const montantTTC = montantHT + montantTVA;
      
      // Construire le libellé complet avec toutes les infos du produit
      let libelleComplet = produit.nom;
      if (produit.reference) {
        libelleComplet += `\nRéférence: ${produit.reference}`;
      }
      if (produit.marque) {
        libelleComplet += `\nMarque: ${produit.marque}`;
      }
      if (produit.description) {
        libelleComplet += `\n${produit.description}`;
      }
      
      const updatedItems = [...newDocument.items];
      updatedItems[index] = {
        ...updatedItems[index],
        produit_id: produitId,
        libelle: libelleComplet,
        prix_unitaire: prixHT,
        taux_tva: tauxTVA,
        montant_ht: montantHT,
        montant_tva: montantTVA,
        montant_ttc: montantTTC
      };
      
      setNewDocument({
        ...newDocument,
        items: updatedItems
      });
    }
  };

  const updateItem = (index, field, value) => {
    const updatedItems = [...newDocument.items];
    updatedItems[index][field] = value;

    // Recalculate amounts for this item
    const item = updatedItems[index];
    const quantite = parseFloat(item.quantite) || 0;
    const prix_unitaire = parseFloat(item.prix_unitaire) || 0;
    const taux_tva = parseFloat(item.taux_tva) || 0;

    item.montant_ht = quantite * prix_unitaire;
    item.montant_tva = item.montant_ht * (taux_tva / 100);
    item.montant_ttc = item.montant_ht + item.montant_tva;

    setNewDocument({
      ...newDocument,
      items: updatedItems
    });
  };

  const removeItem = (index) => {
    const updatedItems = newDocument.items.filter((_, i) => i !== index);
    setNewDocument({
      ...newDocument,
      items: updatedItems
    });
  };

  const handleClientSelect = (value) => {
    if (value === "add-new-client") {
      setShowAddClientDialog(true);
    } else {
      setNewDocument({...newDocument, client_id: value});
    }
  };

  const handleSupplierSelect = (value) => {
    if (value === "add-new-supplier") {
      setShowAddSupplierDialog(true);
    } else {
      setNewDocument({...newDocument, supplier_id: value});
    }
  };

  const handleAddClient = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/clients`, newClient, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setClients([...clients, response.data]);
      setNewDocument({...newDocument, client_id: response.data.id});
      setShowAddClientDialog(false);
      setNewClient({
        company_name: "",
        prenom: "",
        nom: "",
        email: "",
        siren: "",
        adresse: "",
        code_postal: "",
        ville: ""
      });
      toast.success("Client ajouté avec succès");
    } catch (error) {
      console.error("Error adding client:", error);
      toast.error("Erreur lors de l'ajout du client");
    }
  };

  // Supprimer un document
  const handleDeleteDocument = async (docId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce document ? Cette action est irréversible.')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API}/api/devis-factures/${docId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDocuments(documents.filter(doc => doc.id !== docId));
        setShowDetailsDialog(false);
        toast.success('Document supprimé avec succès');
      } catch (error) {
        console.error('Error deleting document:', error);
        toast.error('Erreur lors de la suppression du document');
      }
    }
  };

  // Modifier un document  
  const handleEditDocument = (doc) => {
    setNewDocument({
      type: doc.type,
      recipient_type: doc.recipient_type,
      client_id: doc.client_id || "",
      supplier_id: doc.supplier_id || "",
      date_echeance: doc.date_echeance || "",
      notes: doc.notes || "",
      items: doc.items || []
    });
    setSelectedDocument(doc);
    setShowDetailsDialog(false);
    setShowCreateDialog(true);
  };

  const handleAddSupplier = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Structure adaptée aux fournisseurs de configuration
      const newSupplierData = {
        name: newSupplier.name,
        logo_url: "",
        market_price: 0.090, // Prix par défaut
        upfront_percentage: 20,
        monthly_payment: 150,
        contract_duration: 24,
        margin_rate: 8.0,
        p0_margin: 0.015,
        payment_mode: "upfront_monthly",
        split_payment_delay: 3,
        is_active: true // Nouveau fournisseur actif par défaut
      };

      // Créer le fournisseur via l'API
      const response = await axios.post(`${API}/api/config/suppliers`, newSupplierData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Recharger la liste des fournisseurs
      await fetchSuppliers();
      
      // Sélectionner automatiquement le nouveau fournisseur
      setNewDocument({...newDocument, supplier_id: response.data.id});
      setShowAddSupplierDialog(false);
      setNewSupplier({
        name: "",
        address: "",
        email: "",
        type: "electricity_gas"
      });
      
      toast.success("Fournisseur ajouté avec succès");
    } catch (error) {
      console.error("Error adding supplier:", error);
      toast.error("Erreur lors de l'ajout du fournisseur");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "brouillon":
        return "bg-gray-100 text-gray-800";
      case "envoye":
        return "bg-blue-100 text-blue-800";
      case "accepte":
        return "bg-green-100 text-green-800";
      case "refuse":
        return "bg-red-100 text-red-800";
      case "paye":
        return "bg-emerald-100 text-emerald-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      brouillon: "Brouillon",
      envoye: "Envoyé",
      accepte: "Accepté",
      refuse: "Refusé",
      paye: "Payé"
    };
    return labels[status] || status;
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = (
      doc.numero?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.recipient_nom?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    const matchesType = filterType === "all" || doc.type === filterType;
    
    return matchesSearch && matchesType;
  });

  const calculateTotals = () => {
    const sousTotal = newDocument.items.reduce((sum, item) => sum + (item.montant_ht || 0), 0);
    const totalTva = newDocument.items.reduce((sum, item) => sum + (item.montant_tva || 0), 0);
    const totalTtc = newDocument.items.reduce((sum, item) => sum + (item.montant_ttc || 0), 0);
    
    return { sousTotal, totalTva, totalTtc };
  };

  const { sousTotal, totalTva, totalTtc } = calculateTotals();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Devis & Factures</h1>
          <p className="text-gray-600 mt-2">
            Gérer vos devis et factures clients
          </p>
        </div>
        
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau Document
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedDocument && selectedDocument.id ? "Modifier" : "Créer un"} {newDocument.type}</DialogTitle>
              <DialogDescription>
                {selectedDocument && selectedDocument.id ? "Modifier le" : "Créer un nouveau"} {newDocument.type === "devis" ? "devis" : "facture"} {selectedDocument && selectedDocument.id ? "" : "pour votre client"}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Type Selection */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Type de document</Label>
                  <Select 
                    value={newDocument.type} 
                    onValueChange={(value) => setNewDocument({...newDocument, type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="devis">Devis</SelectItem>
                      <SelectItem value="facture">Facture</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Facturer à</Label>
                  <Select 
                    value={newDocument.recipient_type} 
                    onValueChange={(value) => setNewDocument({
                      ...newDocument, 
                      recipient_type: value,
                      client_id: "",
                      supplier_id: ""
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fournisseur">
                        🏢 Fournisseur d'énergie (Commission)
                      </SelectItem>
                      <SelectItem value="client">
                        👤 Client (Service de courtage)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>
                    {newDocument.recipient_type === "fournisseur" ? "Fournisseur" : "Client"}
                  </Label>
                  {newDocument.recipient_type === "fournisseur" ? (
                    <Select 
                      value={newDocument.supplier_id} 
                      onValueChange={handleSupplierSelect}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un fournisseur" />
                      </SelectTrigger>
                      <SelectContent>
                        {suppliers.map((supplier) => (
                          <SelectItem key={supplier.id} value={supplier.id}>
                            {supplier.name} {supplier.is_active ? '(Actif)' : '(Inactif)'}
                          </SelectItem>
                        ))}
                        <SelectItem value="add-new-supplier">
                          <span className="flex items-center text-blue-600">
                            <Plus className="w-4 h-4 mr-1" />
                            Ajouter un nouveau fournisseur
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex space-x-2">
                        <Button
                          type="button"
                          variant={!manualClientEntry ? "default" : "outline"}
                          size="sm"
                          onClick={() => setManualClientEntry(false)}
                        >
                          Sélectionner
                        </Button>
                        <Button
                          type="button"
                          variant={manualClientEntry ? "default" : "outline"}
                          size="sm"
                          onClick={() => setManualClientEntry(true)}
                        >
                          Saisie manuelle
                        </Button>
                      </div>
                      
                      {!manualClientEntry ? (
                        <Select 
                          value={newDocument.client_id} 
                          onValueChange={handleClientSelect}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner un client" />
                          </SelectTrigger>
                          <SelectContent>
                            {clients.map((client) => (
                              <SelectItem key={client.id} value={client.id}>
                                {client.company_name} - {client.prenom} {client.nom}
                              </SelectItem>
                            ))}
                            <SelectItem value="add-new-client">
                              <span className="flex items-center text-blue-600">
                                <Plus className="w-4 h-4 mr-2" />
                                Ajouter un nouveau client
                              </span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label>Prénom</Label>
                            <Input
                              value={newDocument.manual_client_prenom}
                              onChange={(e) => setNewDocument({...newDocument, manual_client_prenom: e.target.value})}
                              placeholder="Prénom"
                            />
                          </div>
                          <div>
                            <Label>Nom</Label>
                            <Input
                              value={newDocument.manual_client_nom}
                              onChange={(e) => setNewDocument({...newDocument, manual_client_nom: e.target.value})}
                              placeholder="Nom"
                            />
                          </div>
                          <div className="col-span-2">
                            <Label>Raison sociale (optionnel)</Label>
                            <Input
                              value={newDocument.manual_client_company}
                              onChange={(e) => setNewDocument({...newDocument, manual_client_company: e.target.value})}
                              placeholder="Nom de l'entreprise"
                            />
                          </div>
                          <div className="col-span-2">
                            <Label>Adresse</Label>
                            <Input
                              value={newDocument.manual_client_adresse}
                              onChange={(e) => setNewDocument({...newDocument, manual_client_adresse: e.target.value})}
                              placeholder="Adresse complète"
                            />
                          </div>
                          <div>
                            <Label>Code postal</Label>
                            <Input
                              value={newDocument.manual_client_code_postal}
                              onChange={(e) => setNewDocument({...newDocument, manual_client_code_postal: e.target.value})}
                              placeholder="75000"
                            />
                          </div>
                          <div>
                            <Label>Ville</Label>
                            <Input
                              value={newDocument.manual_client_ville}
                              onChange={(e) => setNewDocument({...newDocument, manual_client_ville: e.target.value})}
                              placeholder="Paris"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Date d'échéance */}
              <div>
                <Label>Date d'échéance</Label>
                <Input
                  type="date"
                  value={newDocument.date_echeance}
                  onChange={(e) => setNewDocument({...newDocument, date_echeance: e.target.value})}
                />
              </div>

              {/* Items */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-lg font-semibold">Articles / Services</Label>
                  <Button onClick={addItem} variant="outline" size="sm">
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter un article
                  </Button>
                </div>

                {newDocument.items.map((item, index) => (
                  <Card key={index}>
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        {/* NOUVEAU: Sélecteur de produit depuis le catalogue */}
                        <div>
                          <Label>Choisir depuis le catalogue (optionnel)</Label>
                          <Select 
                            value={item.produit_id || undefined} 
                            onValueChange={(value) => {
                              if (value === "manual") {
                                // Réinitialiser pour saisie manuelle
                                const updatedItems = [...newDocument.items];
                                updatedItems[index] = {
                                  ...updatedItems[index],
                                  produit_id: "",
                                  libelle: "",
                                  prix_unitaire: 0,
                                  taux_tva: 20
                                };
                                setNewDocument({...newDocument, items: updatedItems});
                              } else {
                                selectProduitForItem(index, value);
                              }
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="-- Sélectionner un produit --" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="manual">-- Saisie manuelle --</SelectItem>
                              {produits.map((produit) => (
                                <SelectItem key={produit.id} value={produit.id}>
                                  {produit.nom} - {produit.prix_vente_ht}€ HT
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {/* Champs existants */}
                        <div className="grid grid-cols-12 gap-2 items-end">
                          <div className="col-span-4">
                            <Label>Libellé</Label>
                            <Input
                              value={item.libelle}
                              onChange={(e) => updateItem(index, 'libelle', e.target.value)}
                              placeholder="Description du produit/service"
                            />
                          </div>
                        <div className="col-span-2">
                          <Label>Quantité</Label>
                          <Input
                            type="number"
                            value={item.quantite}
                            onChange={(e) => updateItem(index, 'quantite', e.target.value)}
                            min="0"
                            step="0.01"
                          />
                        </div>
                        <div className="col-span-2">
                          <Label>Prix unitaire HT</Label>
                          <Input
                            type="number"
                            value={item.prix_unitaire}
                            onChange={(e) => updateItem(index, 'prix_unitaire', e.target.value)}
                            min="0"
                            step="0.01"
                          />
                        </div>
                        <div className="col-span-2">
                          <Label>TVA (%)</Label>
                          <Select 
                            value={item.taux_tva.toString()} 
                            onValueChange={(value) => updateItem(index, 'taux_tva', parseFloat(value))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0">0%</SelectItem>
                              <SelectItem value="5.5">5,5%</SelectItem>
                              <SelectItem value="10">10%</SelectItem>
                              <SelectItem value="20">20%</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-1">
                          <Label>Total TTC</Label>
                          <div className="text-sm font-semibold text-blue-600 p-2">
                            {(parseFloat(item.montant_ttc) || 0).toFixed(2)}€
                          </div>
                        </div>
                        <div className="col-span-1">
                          <Button 
                            onClick={() => removeItem(index)} 
                            variant="outline" 
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {/* Totaux */}
                {newDocument.items.length > 0 && (
                  <Card className="bg-gray-50">
                    <CardContent className="p-4">
                      <div className="flex justify-end space-x-8">
                        <div className="text-right">
                          <div className="text-sm text-gray-600">Sous-total HT</div>
                          <div className="font-semibold">{sousTotal.toFixed(2)}€</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-600">Total TVA</div>
                          <div className="font-semibold">{totalTva.toFixed(2)}€</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-600">Total TTC</div>
                          <div className="text-lg font-bold text-blue-600">{totalTtc.toFixed(2)}€</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Notes */}
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={newDocument.notes}
                  onChange={(e) => setNewDocument({...newDocument, notes: e.target.value})}
                  placeholder="Notes supplémentaires..."
                  rows={3}
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowCreateDialog(false)}
                >
                  Annuler
                </Button>
                <Button onClick={handleCreateDocument}>
                  {selectedDocument && selectedDocument.id ? "Modifier" : "Créer"} le {newDocument.type}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search and Filters */}
      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Rechercher par numéro ou client..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les documents</SelectItem>
            <SelectItem value="devis">Devis uniquement</SelectItem>
            <SelectItem value="facture">Factures uniquement</SelectItem>
          </SelectContent>
        </Select>

        <div className="text-sm text-gray-500">
          {filteredDocuments.length} document(s) trouvé(s)
        </div>
      </div>

      {/* Documents Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredDocuments.map((doc) => (
          <Card key={doc.id} className="hover-lift cursor-pointer">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-lg font-semibold text-gray-900 mb-1 flex items-center">
                    {doc.type === "devis" ? (
                      <FileText className="w-5 h-5 mr-2 text-blue-600" />
                    ) : (
                      <Receipt className="w-5 h-5 mr-2 text-green-600" />
                    )}
                    {doc.numero}
                  </CardTitle>
                  <CardDescription className="text-sm text-gray-600">
                    {doc.recipient_nom}
                  </CardDescription>
                </div>
                <Badge className={getStatusColor(doc.status)}>
                  {getStatusLabel(doc.status)}
                </Badge>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-gray-500">Date création</div>
                  <div className="font-medium">
                    {new Date(doc.date_creation).toLocaleDateString('fr-FR')}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Échéance</div>
                  <div className="font-medium">
                    {doc.date_echeance ? new Date(doc.date_echeance).toLocaleDateString('fr-FR') : "N/A"}
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-3 rounded">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total TTC</span>
                  <span className="text-lg font-bold text-blue-600">
                    {new Intl.NumberFormat('fr-FR', {
                      style: 'currency',
                      currency: 'EUR'
                    }).format(doc.total_ttc)}
                  </span>
                </div>
              </div>

              <div className="flex space-x-2">
                <Button 
                  onClick={() => {
                    setSelectedDocument(doc);
                    setShowDetailsDialog(true);
                  }}
                  className="flex-1"
                  variant="outline"
                  size="sm"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  Voir
                </Button>
                
                {/* NOUVEAU: Bouton Modifier direct */}
                <Button 
                  onClick={() => {
                    setSelectedDocument(doc);
                    // TODO: setShowEditDialog not yet implemented
                  }}
                  variant="outline"
                  size="sm"
                  title="Modifier"
                >
                  <Edit className="w-4 h-4" />
                </Button>
                
                {/* NOUVEAU: Bouton Supprimer direct */}
                <Button 
                  onClick={() => {
                    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${doc.numero} ?`)) {
                      // TODO: deleteDocument not yet implemented
                    }
                  }}
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:text-red-700"
                  title="Supprimer"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
                
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => generatePDF(doc)}
                  title="Télécharger PDF"
                >
                  <Download className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredDocuments.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Aucun document trouvé
            </h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || filterType !== "all" 
                ? "Aucun document ne correspond à vos critères de recherche."
                : "Créez votre premier devis ou facture pour commencer."
              }
            </p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Créer un document
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Document Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedDocument && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center">
                  {selectedDocument.type === "devis" ? (
                    <FileText className="w-5 h-5 mr-2 text-blue-600" />
                  ) : (
                    <Receipt className="w-5 h-5 mr-2 text-green-600" />
                  )}
                  {selectedDocument.numero}
                </DialogTitle>
                <DialogDescription>
                  Détails du {selectedDocument.type} pour {selectedDocument.recipient_nom}
                </DialogDescription>
              </DialogHeader>
              
              {/* Document preview would go here */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold mb-2">Informations client</h4>
                    <div className="text-sm space-y-1">
                      <div>{selectedDocument.recipient_nom}</div>
                      <div>{selectedDocument.recipient_adresse}</div>
                      <div>{selectedDocument.recipient_code_postal} {selectedDocument.recipient_ville}</div>
                      <div>{selectedDocument.recipient_email}</div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold mb-2">Détails du document</h4>
                    <div className="text-sm space-y-1">
                      <div>Numéro: {selectedDocument.numero}</div>
                      <div>Date: {new Date(selectedDocument.date_creation).toLocaleDateString('fr-FR')}</div>
                      <div>Échéance: {selectedDocument.date_echeance ? new Date(selectedDocument.date_echeance).toLocaleDateString('fr-FR') : "N/A"}</div>
                      <div>Statut: <Badge className={getStatusColor(selectedDocument.status)}>{getStatusLabel(selectedDocument.status)}</Badge></div>
                    </div>
                  </div>
                </div>

                {/* Items */}
                <div>
                  <h4 className="font-semibold mb-2">Articles</h4>
                  <div className="space-y-2">
                    {selectedDocument.items.map((item, index) => (
                      <div key={index} className="grid grid-cols-6 gap-2 text-sm p-2 bg-gray-50 rounded">
                        <div className="col-span-2">{item.libelle}</div>
                        <div>{item.quantite}</div>
                        <div>{(parseFloat(item.prix_unitaire) || 0).toFixed(2)}€</div>
                        <div>{(parseFloat(item.taux_tva) || 0)}%</div>
                        <div className="text-right font-semibold">{(parseFloat(item.montant_ttc) || 0).toFixed(2)}€</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Totaux */}
                <div className="bg-gray-100 p-4 rounded">
                  <div className="flex justify-end space-x-8">
                    <div className="text-right">
                      <div className="text-sm text-gray-600">Sous-total HT</div>
                      <div className="font-semibold">{(parseFloat(selectedDocument.sous_total_ht) || 0).toFixed(2)}€</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-600">Total TVA</div>
                      <div className="font-semibold">{(parseFloat(selectedDocument.total_tva) || 0).toFixed(2)}€</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-600">Total TTC</div>
                      <div className="text-lg font-bold text-blue-600">{(parseFloat(selectedDocument.total_ttc) || 0).toFixed(2)}€</div>
                    </div>
                  </div>
                </div>

                {selectedDocument.notes && (
                  <div>
                    <h4 className="font-semibold mb-2">Notes</h4>
                    <p className="text-sm text-gray-600">{selectedDocument.notes}</p>
                  </div>
                )}

                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => handleEditDocument(selectedDocument)}>
                    <Edit className="w-4 h-4 mr-2" />
                    Modifier
                  </Button>
                  <Button variant="outline" onClick={() => generatePDF(selectedDocument)}>
                    <Download className="w-4 h-4 mr-2" />
                    Télécharger PDF
                  </Button>
                  <Button variant="outline" onClick={() => handleDeleteDocument(selectedDocument.id)} className="text-red-600 hover:text-red-700">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer
                  </Button>
                  <Button>
                    <Send className="w-4 h-4 mr-2" />
                    Envoyer
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Client Dialog */}
      <Dialog open={showAddClientDialog} onOpenChange={setShowAddClientDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Ajouter un nouveau client</DialogTitle>
            <DialogDescription>
              Créer un nouveau client pour la facturation
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Nom de l'entreprise</Label>
              <Input
                value={newClient.company_name}
                onChange={(e) => setNewClient({...newClient, company_name: e.target.value})}
                placeholder="Nom de l'entreprise"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Prénom</Label>
                <Input
                  value={newClient.prenom}
                  onChange={(e) => setNewClient({...newClient, prenom: e.target.value})}
                  placeholder="Prénom"
                />
              </div>
              <div>
                <Label>Nom</Label>
                <Input
                  value={newClient.nom}
                  onChange={(e) => setNewClient({...newClient, nom: e.target.value})}
                  placeholder="Nom"
                />
              </div>
            </div>
            
            <div>
              <Label>SIREN</Label>
              <Input
                value={newClient.siren}
                onChange={(e) => setNewClient({...newClient, siren: e.target.value})}
                placeholder="123456789"
              />
            </div>
            
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={newClient.email}
                onChange={(e) => setNewClient({...newClient, email: e.target.value})}
                placeholder="email@example.com"
              />
            </div>
            
            <div>
              <Label>Adresse</Label>
              <Input
                value={newClient.adresse}
                onChange={(e) => setNewClient({...newClient, adresse: e.target.value})}
                placeholder="Adresse complète"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Code postal</Label>
                <Input
                  value={newClient.code_postal}
                  onChange={(e) => setNewClient({...newClient, code_postal: e.target.value})}
                  placeholder="69000"
                />
              </div>
              <div>
                <Label>Ville</Label>
                <Input
                  value={newClient.ville}
                  onChange={(e) => setNewClient({...newClient, ville: e.target.value})}
                  placeholder="Lyon"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowAddClientDialog(false)}>
                Annuler
              </Button>
              <Button onClick={handleAddClient}>
                Ajouter le client
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Supplier Dialog */}
      <Dialog open={showAddSupplierDialog} onOpenChange={setShowAddSupplierDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Ajouter un nouveau fournisseur</DialogTitle>
            <DialogDescription>
              Créer un nouveau fournisseur d'énergie pour la facturation
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Nom du fournisseur</Label>
              <Input
                value={newSupplier.name}
                onChange={(e) => setNewSupplier({...newSupplier, name: e.target.value})}
                placeholder="Nom du fournisseur"
              />
            </div>
            
            <div>
              <Label>Type d'énergie</Label>
              <Select 
                value={newSupplier.type} 
                onValueChange={(value) => setNewSupplier({...newSupplier, type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="electricity">Électricité uniquement</SelectItem>
                  <SelectItem value="electricity_gas">Électricité et Gaz</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Adresse</Label>
              <Textarea
                value={newSupplier.address}
                onChange={(e) => setNewSupplier({...newSupplier, address: e.target.value})}
                placeholder="Adresse complète du fournisseur"
                rows={2}
              />
            </div>
            
            <div>
              <Label>Email de facturation</Label>
              <Input
                type="email"
                value={newSupplier.email}
                onChange={(e) => setNewSupplier({...newSupplier, email: e.target.value})}
                placeholder="facturation@fournisseur.com"
              />
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowAddSupplierDialog(false)}>
                Annuler
              </Button>
              <Button onClick={handleAddSupplier}>
                Ajouter le fournisseur
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DevisFacturesPage;